import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { stepRolesQuery } from '@/entities/approval-chain';
import { projectRoleAssignmentsQuery } from '@/entities/project-member';

import type { StepRoleCatalogue } from '../ApproverPicker';

/**
 * The roles the project's steps may name, and who holds each. Who holds a role is read only
 * once the roles are, because the platform shows both to the same callers.
 */
export function useStepRoleCatalogue(projectId: string): StepRoleCatalogue {
  const rolesQuery = useQuery(stepRolesQuery(projectId));
  const assignmentsOptions = projectRoleAssignmentsQuery(projectId);
  const assignmentsQuery = useQuery({
    ...assignmentsOptions,
    enabled: assignmentsOptions.enabled && rolesQuery.isSuccess,
  });

  return useMemo<StepRoleCatalogue>(() => {
    if (rolesQuery.isPending) return { kind: 'loading' };
    if (rolesQuery.isError) return { kind: 'unavailable' };
    return {
      kind: 'ready',
      roles: rolesQuery.data,
      holders: assignmentsQuery.data ? holdersByRole(assignmentsQuery.data) : null,
    };
  }, [rolesQuery.isPending, rolesQuery.isError, rolesQuery.data, assignmentsQuery.data]);
}

function holdersByRole(
  assignments: { roleId: string; identityId: string }[],
): ReadonlyMap<string, ReadonlySet<string>> {
  const byRole = new Map<string, Set<string>>();
  for (const assignment of assignments) {
    const holders = byRole.get(assignment.roleId) ?? new Set<string>();
    holders.add(assignment.identityId);
    byRole.set(assignment.roleId, holders);
  }
  return byRole;
}
